import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { InventoryCheckboxFilterModel } from 'src/app/models/results/inventory-checkbox-filter';
import { InventoryItemGroup } from 'src/app/models/results/inventory-item-group';
import { InventorySearchResult } from 'src/app/models/results/inventory-search-result';
import { PriceSort } from 'src/app/models/results/price-sort';
import { ShareResultsInfo } from 'src/app/models/results/share-results-info';
import { WizardOption } from 'src/app/models/wizard/wizard-option';
import { WizardStep } from 'src/app/models/wizard/wizard-step';
import { AnalyticsService, AnalyticsUtility } from 'src/app/services/analytics/analytics.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { ResultsService } from 'src/app/services/results/results.service';
import { ILocaleInfo, UtilityService } from 'src/app/services/utility.service';
import { WizardStepName } from 'src/app/services/wizard/wizard-step-name.enum';
import { WizardService } from 'src/app/services/wizard/wizard.service';
import { constants } from 'src/environments/constants';
import { environment } from 'src/environments/environment';
import { ShareResultsDialogComponent } from './sections/share-results-dialog/share-results-dialog.component';

declare let MktoForms2: any;

@Component({
  selector: 'app-results-step',
  templateUrl: './results-step.component.html',
  styleUrls: ['./results-step.component.scss']
})
export class ResultsStepComponent implements OnInit {
  currentStep: WizardStep = new WizardStep();
  stepNumber: number = 0;
  title: string = "";
  subtitle: string = "";
  shareResultsButtonText: string = "";
  startOverButtonText: string = "";
  loadMoreButtonText: string = "";
  filtersTitle: string = "";
  selectedPrimaryUse: string = "";
  filterItemsFromCheckboxParams: InventoryCheckboxFilterModel = new InventoryCheckboxFilterModel;
  showNoResultsMessage: boolean = false;
  noResultsMessage: string = "";
  showTopNumberOfItemsMessage: boolean = false;
  topNumberOfItemsMessage: string = "";
  topNumberOfItemsMessageMobile: string = "";
  deviceResultGroups: InventoryItemGroup[] = [];
  displayedDeviceGroups: InventoryItemGroup[] = [];
  numberOfDevicesPerLoad: number = 0;
  enableLoadMore: boolean = false;
  analyticsStepName: string = AnalyticsUtility.wizardStepName.results;
  resultsLoading: boolean = false;
  showShareResultsButton: boolean = false;
  marketoFormId: string = "";
  isEnterprisePrimaryUse: boolean = false;
  showMobileFilters: boolean = false;
  selectedFiltersCount: number = 0;
  isNonEnglishLocale: boolean = false;
  // only gets set when a user changes the price sorting, otherwise default sorting will be used
  selectedPriceSort?: PriceSort = undefined;
  showAUELink: boolean = true;
  showSaveResultsButton: boolean = false;

  constructor(
    public wizardService: WizardService,
    private resultsService: ResultsService,
    private utilityService: UtilityService,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private confSvc: ConfigService,
    private analyticsService: AnalyticsService,
    private router: Router) {}

  async ngOnInit(): Promise<void> {
    this.loadMarketoForm();

    await this.confSvc.setup();

    this.currentStep = this.wizardService.getStep(WizardStepName.Results);
    this.stepNumber = this.wizardService.getStepNumber(this.currentStep.name);
    this.title = this.confSvc.getString("resultsTitle");
    this.subtitle = this.confSvc.getString("resultsSubtitle");
    this.noResultsMessage = this.confSvc.getString("noResultsMessage");
    this.topNumberOfItemsMessage = this.confSvc.getString("topNumberOfItemsMessage");
    this.topNumberOfItemsMessageMobile = this.confSvc.getString("topNumberOfItemsMessageMobile");
    this.numberOfDevicesPerLoad = this.confSvc.getNum("deviceResultsToDisplay");
    this.shareResultsButtonText = this.confSvc.getString("shareResultsButtonText");
    this.startOverButtonText = this.confSvc.getString("startOverButtonText");
    this.loadMoreButtonText = this.confSvc.getString("loadMoreButtonText");
    this.filtersTitle = this.confSvc.getString("filtersTitle");
    this.showShareResultsButton = this.confSvc.getBool("showShareResultsButton");;
    this.isNonEnglishLocale = !this.utilityService.getLocaleInfo().isEnglish;
    this.showAUELink = this.confSvc.getBool("showAUELink");
    this.showSaveResultsButton = this.confSvc.getBool("showSaveResultsButton");

    this.route.queryParams.subscribe(params => {
      this.wizardService.setSelectedOptionsFromUrlIfPresent(params);
      this.searchForDevices();
    });
    this.analyticsService.logCurrentPage(this.analyticsStepName);

  }

  searchForDevices(filterItemsFromCheckboxParams?: InventoryCheckboxFilterModel): void {
    this.resultsLoading = true;

    this.wizardService.getSelectedWizardOptionsForStep(WizardStepName.PrimaryUse)
      .subscribe((selectedOption: WizardOption[]) => {
        this.selectedPrimaryUse = selectedOption[0]?.title ? selectedOption[0].title : "";
        if(this.selectedPrimaryUse === this.confSvc.getString("primaryUseEducationTitle")) {
          this.resultsService.getEducationInventoryItems(filterItemsFromCheckboxParams, this.selectedPriceSort)
            .subscribe(result => {
              this.setDeviceResults(result);
            });
        } else {
          this.isEnterprisePrimaryUse = true;
          this.resultsService.getEnterpriseInventoryItems(filterItemsFromCheckboxParams, this.selectedPriceSort)
          .subscribe(result => {
            this.setDeviceResults(result);
          });
        }
    });
  }

  setDeviceResults(result: InventorySearchResult): void {
    this.resultsLoading = false;
    this.showMessageBasedOnResults(result);
    this.deviceResultGroups = result.itemGroups;
    this.displayedDeviceGroups = this.getNextItemsForDisplay();
    this.enableLoadMore = result.enableLoadMore;
    this.analyticsService.logResultsLoaded(result.itemGroups);
  }

  loadMoreDevices(): void {
    this.showTopNumberOfItemsMessage = false;
    this.displayedDeviceGroups.push(...this.getNextItemsForDisplay());

    if (this.deviceResultGroups.length === 0) {
      this.enableLoadMore = false;
    }
  }

  getNextItemsForDisplay(): InventoryItemGroup[] {
    return this.deviceResultGroups.splice(0, this.numberOfDevicesPerLoad);
  }

  showMessageBasedOnResults(result: InventorySearchResult): void {
    this.showNoResultsMessage = (result.itemGroups.length === 0);
    this.showTopNumberOfItemsMessage = result.enableLoadMore;
  }

  openPricingCalculator(): void {
    this.utilityService.openUrlInNewTab(this.confSvc.getString("pricingCalculatorURL"));
  }

  setInitialSelectedBrands(items: string[]): void {
    // the assignment for this and the other filters use the spread operator for items so that change detection will be triggered for components that have these filters as Inputs
    this.filterItemsFromCheckboxParams.brands = [...items];
    this.updateSelectedFiltersCount();
  }

  handleBrandsFilter(items: string[]): void {
    this.filterItemsFromCheckboxParams.brands = [...items];
    this.updateSelectedFiltersCount();
    this.searchForDevices(this.filterItemsFromCheckboxParams);
  }

  handleScreenSizeFilter(items: number[]): void {
    this.filterItemsFromCheckboxParams.screenSizes = [...items];
    this.updateSelectedFiltersCount();
    this.searchForDevices(this.filterItemsFromCheckboxParams);
  }

  // Note: The storage-filters component is currently not in the UI. Leaving the logic so that it can easily be added back if the client requests it.
  handleStorageSizeFilter(items: number[]): void {
    this.filterItemsFromCheckboxParams.storageSizes = [...items];
    this.updateSelectedFiltersCount();
    this.searchForDevices(this.filterItemsFromCheckboxParams);
  }

  handleFormFactorFilter(items: string[]): void {
    this.filterItemsFromCheckboxParams.formFactors = [...items];
    this.updateSelectedFiltersCount();
    this.searchForDevices(this.filterItemsFromCheckboxParams);
  }

  handleFeatureFilter(items: string[]): void {
    this.filterItemsFromCheckboxParams.features = [...items];
    this.updateSelectedFiltersCount();
    this.searchForDevices(this.filterItemsFromCheckboxParams);
  }

  setInitialSelectedOsTypes(items: string[]): void {
    this.filterItemsFromCheckboxParams.osTypes = [...items];
    this.updateSelectedFiltersCount();
  }

  handleOsTypeFilter(items: string[]): void {
    this.filterItemsFromCheckboxParams.osTypes = [...items];
    this.updateSelectedFiltersCount();
    this.searchForDevices(this.filterItemsFromCheckboxParams);
  }

  handlePriceSorting(priceSort: PriceSort): void {
    this.selectedPriceSort = priceSort;
    this.searchForDevices(this.filterItemsFromCheckboxParams);
  }

  handleShareResultsClick(): void {
    this.analyticsService.logShareResultsClick();

    switch (environment.name) {
      case constants.INSIGHT:
      case constants.INSIGHT_EDU:
        const dialogRef = this.dialog.open(ShareResultsDialogComponent, {
          position: {top: '25px'},
          data: {showConfirmationMessage: false}
        });

        dialogRef.afterClosed().subscribe((shareResultsInfo: ShareResultsInfo) => {
          if (shareResultsInfo) {
            this.submitMarketoForm(shareResultsInfo);
          }
        });
        break;
      default:
        console.log("share results clicked");
        break;
    }
  }

  handleShowSaveResultsClick(): void {
    window.print()
  }

  loadMarketoForm(): void {
    if ([constants.INSIGHT, constants.INSIGHT_EDU].includes(environment.name)) {
      const localeInfo: ILocaleInfo = this.utilityService.getLocaleInfo();

      switch (localeInfo.localeName) {
        case constants.EN_US_LOCALE:
          this.marketoFormId = "10951";
          MktoForms2.loadForm("//pages.insight.com", "366-UKY-221", 10951);
          break;
        case constants.FR_CA_LOCALE:
          this.marketoFormId = "12268";
          MktoForms2.loadForm("//pages.insight.com", "366-UKY-221", 12268);
          break;
        case constants.EN_CA_LOCALE:
          this.marketoFormId = "12267";
          MktoForms2.loadForm("//pages.insight.com", "366-UKY-221", 12267);
          break;
        case constants.EN_GB_LOCALE:
          this.marketoFormId = "1229";
          MktoForms2.loadForm("//app-lon10.marketo.com", "420-FAJ-420", 1229);
          break;
        default:
          break;
      }
    }
  }

  submitMarketoForm(shareResultsInfo: ShareResultsInfo): void {
    var marketoForm = MktoForms2.allForms()[0];

    MktoForms2.whenReady(function(marketoForm: any){
      marketoForm.onSuccess(function(){
        return false;
      });
    });

    marketoForm.addHiddenFields({
      //These are the values which will be submitted to Marketo
      "Email": shareResultsInfo.email,
      "FirstName": shareResultsInfo.firstName,
      "LastName": shareResultsInfo.lastName,
      "Company": shareResultsInfo.company,
      "assetURL": shareResultsInfo.resultsPageUrl,
      "optIn": shareResultsInfo.optInToMarketing
      });

    marketoForm.submit();

    this.dialog.open(ShareResultsDialogComponent, {
      position: {top: '25px'},
      data: {showConfirmationMessage: true}
    });
  }

  handleStartOverClick(): void {
    this.analyticsService.logStartOverClick();
    this.wizardService.removeAllSelectedWizardOptions();

    this.wizardService.getStepRoutingQueryString().subscribe((queryString: string) => {
      this.router.navigateByUrl(`/primary-use${queryString}`);
    });
  }

  handleMobileFiltersButtonClick(): void {
    this.showMobileFilters = !this.showMobileFilters;
  }

  updateSelectedFiltersCount(): void {
    let count: number = 0;

    count += this.filterItemsFromCheckboxParams.brands.length;
    count += this.filterItemsFromCheckboxParams.screenSizes.length / 2;
    count += this.filterItemsFromCheckboxParams.formFactors.length;
    count += this.filterItemsFromCheckboxParams.features.length;
    count += this.filterItemsFromCheckboxParams.osTypes.length;

    this.selectedFiltersCount = count;
  }
}
